import { forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { canHaveOwnershipShare } from '../../utils/isGeneralUser';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import '../ConfirmModal/ConfirmModal.css';
import '../modal/ModalBase.css';
import './MembersPanel.css';
import AddMemberModal from './AddMemberModal';
import MemberCard from './MemberCard';
import MembersGroups from './MembersGroups';
import OwnershipSharesModal from './OwnershipSharesModal';
import RestoreMemberModal from './RestoreMemberModal';
import TransferOwnershipModal from './TransferOwnershipModal';
import { useMembersPanel } from './hooks/useMembersPanel';
import type { User } from '../../types';

export type MembersPanelHandle = {
  openAdd: () => void;
  openShares: (aircraftId: number) => void;
};

const MembersPanel = forwardRef<MembersPanelHandle>(function MembersPanel(
  _,
  ref
) {
  const model = useMembersPanel();
  const navigate = useNavigate();

  const renderMemberCard = (u: User) => (
    <MemberCard
      key={u.id}
      member={u}
      currentUserId={model.currentUserId}
      actorClubRole={model.actorClubRole}
      busyId={model.busyId}
      joinedDrafts={model.joinedDrafts}
      roleOptions={model.roleOptions}
      onOwnerPilotToggle={(nextIsPilot) =>
        model.openSharesModal(model.members, [], null, null, null, null, nextIsPilot)
      }
      onRoleChange={(role) => {
        void model.handleRoleChange(u, role);
      }}
      onJoinedFocus={() =>
        model.setJoinedDrafts((prev) => ({
          ...prev,
          [u.id]: u.joined_on || '',
        }))
      }
      onJoinedChange={(value) =>
        model.setJoinedDrafts((prev) => ({
          ...prev,
          [u.id]: value,
        }))
      }
      onJoinedBlur={(value) => {
        void model.commitJoinedOn(u, value);
      }}
      onActivatePending={() => model.openSharesModal(model.members, [u.id])}
      onRestore={() => model.handleActivate(u)}
      onRemove={() => model.setDeactivating(u)}
      onOpenCabinet={() => {
        navigate(
          u.id === model.currentUserId ? '/profile' : `/profile/${u.id}`
        );
      }}
    />
  );

  useImperativeHandle(ref, () => ({
    openAdd: () => model.setAddOpen(true),
    openShares: (forAircraftId: number) => {
      void model.openSharesForAircraft(forAircraftId);
    },
  }));

  return (
    <div className="members-panel">
      {model.feedbackNode}

      <MembersGroups
        loading={model.loading}
        membersCount={model.members.length}
        owners={model.owners}
        pendingMembers={model.pendingMembers}
        others={model.others}
        isAdmin={model.isAdmin}
        renderMember={renderMemberCard}
      />

      {model.addOpen ? (
        <AddMemberModal
          addTab={model.addTab}
          inviteRole={model.inviteRole}
          inviteLink={model.inviteLink}
          form={model.form}
          adding={model.adding}
          createRoleOptions={model.createRoleOptions}
          onTabChange={model.setAddTab}
          onInviteRoleChange={(role) => {
            model.setInviteRole(role);
            model.setInviteLink(null);
          }}
          onInvite={() => {
            void model.handleInvite();
          }}
          onCopyInvite={() => {
            void model.copyInviteLink();
          }}
          onFormChange={model.setForm}
          onAdd={(e) => {
            void model.handleAdd(e);
          }}
          onClose={model.closeAddModal}
        />
      ) : null}

      {model.sharesModalOpen ? (
        <OwnershipSharesModal
          members={model.modalMembers}
          aircraftList={model.aircraftList}
          sharesAircraftId={model.sharesAircraftId}
          shareDrafts={model.shareDrafts}
          shareDraftsByAircraft={model.shareDraftsByAircraft}
          sharesEffectiveFrom={model.sharesEffectiveFrom}
          sharesSaving={model.sharesSaving}
          sharesLeaveUserId={model.sharesLeaveUserId}
          sharesRoleChange={model.sharesRoleChange}
          sharesOwnerIsPilot={model.sharesOwnerIsPilot}
          lockAircraft={model.sharesAircraftLocked}
          modalShareSumLabel={model.modalShareSumLabel}
          modalShareSumOk={model.modalShareSumOk}
          onClose={model.closeSharesModal}
          onSave={() => {
            void model.saveOwnershipShares();
          }}
          onEffectiveFromChange={model.setSharesEffectiveFrom}
          onShareDraftChange={(userId, value) =>
            model.setShareDrafts((prev) => ({ ...prev, [userId]: value }))
          }
          onStashCurrentDrafts={() =>
            model.setShareDraftsByAircraft((prev) => ({
              ...prev,
              [model.sharesAircraftId]: { ...model.shareDrafts },
            }))
          }
          shareHistory={model.shareHistory}
          historyLoading={model.historyLoading}
          onDeletePeriod={(effectiveFrom) =>
            model.setPeriodToDelete(effectiveFrom)
          }
          onAircraftChange={(next, stored) => {
            model.setSharesAircraftId(next);
            if (stored) {
              model.setShareDrafts(stored);
              model.setSharesModalMemberIds(
                Object.keys(stored).map((id) => Number(id))
              );
            }
          }}
        />
      ) : null}

      {model.deactivating && (
        <ConfirmModal
          title={
            model.deactivating.role === 'guest'
              ? 'Удалить гостя?'
              : 'Удалить участника?'
          }
          message={
            model.deactivating.role === 'guest'
              ? `${model.deactivating.name} будет полностью удалён из базы.`
              : model.deactivating.role === 'technician'
                ? `${model.deactivating.name} будет помечен как неактивный.`
                : canHaveOwnershipShare(
                    model.deactivating.role,
                    model.deactivating.is_pilot
                  )
                  ? `Долю «${model.deactivating.name}» нужно распределить между оставшимися. Откроется окно долей.`
                  : `${model.deactivating.name} будет помечен как неактивный. История полётов сохранится.`
          }
          confirmLabel={
            canHaveOwnershipShare(
              model.deactivating.role,
              model.deactivating.is_pilot
            )
              ? 'Распределить доли'
              : 'Удалить'
          }
          cancelLabel="Отмена"
          danger
          onConfirm={model.handleDeactivateConfirm}
          onCancel={() => model.setDeactivating(null)}
        />
      )}

      {model.transferring && (
        <TransferOwnershipModal
          member={model.transferring}
          actorIsPilot={model.user?.is_pilot}
          submitting={model.busyId === model.transferring.id}
          onConfirm={() => {
            void model.confirmTransferOwnership();
          }}
          onCancel={() => model.setTransferring(null)}
        />
      )}

      {model.periodToDelete ? (
        <ConfirmModal
          title="Удалить период долей?"
          message="Снимок будет удалён. Расходы за эти даты пересчитаются по раннему периоду (или поровну, если более раннего снимка нет)."
          confirmLabel="Удалить"
          cancelLabel="Отмена"
          danger
          submitting={model.sharesSaving}
          onConfirm={() => {
            void model.confirmDeleteSharePeriod();
          }}
          onCancel={() => {
            if (!model.sharesSaving) model.setPeriodToDelete(null);
          }}
        />
      ) : null}

      {model.restoring && (
        <RestoreMemberModal
          name={model.restoring.name}
          restoreRole={model.restoreRole}
          createRoleOptions={model.createRoleOptions}
          onRoleChange={model.setRestoreRole}
          onConfirm={() => void model.handleRestoreConfirm()}
          onCancel={() => model.setRestoring(null)}
        />
      )}
    </div>
  );
});

export default MembersPanel;
